import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { KeyRound, TriangleAlert, Workflow } from 'lucide-react';
import { BroadcastEventType } from '@cotera/api';
import type { DatasetRowKeyCheckResponseV20260601 } from '@cotera/api';
import {
  COTERA_CSV_ROW_NUMBER_FIELD,
  datasetRowIdColumn,
  isInternalDatasetColumn,
  isInternalRowKeyDeclaration,
} from '@cotera/sdk/core';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { DatasetModel } from './dataset.model';

/**
 * What the dataset's kind reported about a candidate key. Computed server-side
 * against the rows themselves — see `DatasetKindStrategy.checkRowKeyUniqueness`.
 */
type RowKeyCheck = DatasetRowKeyCheckResponseV20260601;

/** Whether this key can identify every row. Both counts have to be zero. */
const rowKeyCheckPasses = (check: RowKeyCheck): boolean =>
  check.duplicateRows === 0 && check.unidentifiableRows === 0;

const setDatasetRowKeysParamsSchema = z.object({
  /** In order — a compound key encodes its columns positionally. */
  rowKeyColumns: z.array(z.string().min(1)),
  // A row kebab menu already knows which dataset it targets, so it can pass the
  // id directly rather than relying on the shared selection model.
  datasetId: z.string().optional(),
  /**
   * Offer a way out of the dialog that is not cancelling.
   *
   * Set by a caller that asked for a key on the user's behalf — a re-upload —
   * where declining to pick one is a legitimate answer that should let the
   * caller carry on. Absent when the user opened this deliberately, since there
   * is nothing to continue to.
   */
  allowSkip: z.boolean().optional(),
  /** The user declined to declare a key. Writes nothing. */
  skipped: z.boolean().optional(),
});

type Params = z.infer<typeof setDatasetRowKeysParamsSchema>;

type Errors = { t: string; message?: string }[];

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type SetDatasetRowKeysActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

/**
 * Declares which columns identify a row in a dataset.
 *
 * The one thing that makes row identity survive a re-import. Without it a CSV
 * dataset's rows are addressed by their position in the file, which shifts the
 * moment a row is inserted upstream — so every attached workflow sees a file of
 * brand-new rows and re-runs over all of them, while the runs already recorded
 * detach from the rows that produced them.
 *
 * Always asks, even when the caller supplies columns: choosing a key is a claim
 * about the data that has to be checked against the data, and the dialog is
 * where that check happens.
 */
export class SetDatasetRowKeysAction extends BaseAction<Params, Errors> {
  title = 'Set row keys';
  description = 'Choose the columns that identify a row across re-uploads';
  inputSchema = setDatasetRowKeysParamsSchema;
  group = 'Dataset';
  icon = 'key';

  constructor(
    private readonly client: SetDatasetRowKeysActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }
    // Declining is not cancelling: nothing is written, but the caller that
    // asked for the key on the user's behalf is told to carry on. Cancelling
    // returns `cancelled` instead, which stops it.
    if (payload.skipped === true) {
      return ok({});
    }

    const result = await this.client.resource.dataset.updateSettings({
      id: datasetId,
      rowKeyColumns: payload.rowKeyColumns,
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to set row keys'
          : 'Failed to set row keys';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });

    toast.success(
      payload.rowKeyColumns.length === 0
        ? 'Row keys cleared'
        : `Rows are now identified by ${payload.rowKeyColumns.join(', ')}`
    );
    return ok({});
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    // Resolved before the modal opens: the shared selection model can be
    // mutated while the dialog is up, and the dataset this was invoked for is
    // the one it should still be editing when the user confirms.
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <SetDatasetRowKeysAskView
              datasetId={datasetId}
              allowSkip={params.allowSkip === true}
              onRespond={async (nextParams) => {
                // Carries the resolved id back out, so `execute` never has to
                // ask scope again. It answered on the dataset page, where a
                // DatasetModel is targeted, and nowhere else — which made this
                // action unusable from anywhere that passes the dataset in.
                await onRespond({
                  ...nextParams,
                  ...(datasetId !== null ? { datasetId } : {}),
                });
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private selectedDataset(
    context: ApplicableContext
  ): ResourceSelectionModel | null {
    if (!context.isInScope(ResourceSelectionModel)) {
      return null;
    }
    const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
    const single = selection.length === 1 ? selection[0] : undefined;
    if (
      single === undefined ||
      single.selections.length !== 1 ||
      single.getResourceIdsForType(ResourceType.Dataset).length !== 1
    ) {
      return null;
    }
    return single;
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const selected = this.selectedDataset(context);
    if (selected !== null) {
      return selected.getResourceIdsForType(ResourceType.Dataset)[0] ?? null;
    }
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}

type ArtifactColumn = { name: string; type: string | null };

/**
 * The row-id column as something a user can be shown and can pick.
 *
 * A CSV dataset is given this key at import, so the dialog has to render it or
 * a freshly uploaded dataset would open showing a key it does not admit to
 * having. It is a real option rather than a caption: the honest thing to offer
 * someone who has picked business columns and changed their mind is the key
 * they started with.
 */
const rowIdColumnLabel = (name: string): string =>
  name === COTERA_CSV_ROW_NUMBER_FIELD ? 'Row number' : 'Row id';

/**
 * Picks the key columns and has the dataset's kind prove the choice against the
 * rows.
 *
 * Fetches the dataset itself rather than taking it from a model, so the same
 * dialog works from the dataset page, a resource list row and the command
 * palette alike. The uniqueness question goes to the server for a reason the
 * browser cannot work around: only the kind knows where the rows are, and it
 * keys them with the same expression the trigger evaluator uses.
 */
const SetDatasetRowKeysAskView: React.FC<{
  datasetId: string | null;
  /** Renders the "continue without one" way out. See `allowSkip` on Params. */
  allowSkip: boolean;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, allowSkip, onRespond, onCancel }) => {
  const client = useTenantedClient();
  const [columns, setColumns] = useState<ArtifactColumn[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * An ordered list rather than a set: `resolveRowKey` encodes a compound key
   * positionally, so `[email, org]` and `[org, email]` are different keys and
   * the user's pick order is the one that gets stored.
   */
  const [selected, setSelected] = useState<string[]>([]);
  const [check, setCheck] = useState<RowKeyCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (datasetId === null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await client.resource.get({
        type: ResourceType.Dataset,
        id: datasetId,
      });
      if (cancelled) {
        return;
      }
      if (res.isErr() || res.value.type !== 'dataset') {
        setLoadError('Could not load this dataset');
        return;
      }
      const artifact = res.value.dataset.artifacts?.[0];
      const artifactColumns = artifact?.metadata?.schema.columns ?? [];
      const rowId = datasetRowIdColumn(artifactColumns);
      // The row id leads, and every other bookkeeping column is dropped: those
      // are the product's own and name a row by something that is not its
      // identity, which is why the server refuses them.
      setColumns([
        ...artifactColumns.filter((column) => column.name === rowId),
        ...artifactColumns.filter(
          (column) => !isInternalDatasetColumn(column.name)
        ),
      ]);
      setSelected([...(res.value.dataset.rowKeyColumns ?? [])]);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId]);

  useEffect(() => {
    if (datasetId === null || selected.length === 0) {
      setCheck(null);
      setCheckError(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    setCheckError(null);
    void (async () => {
      const result = await client.resource.dataset.checkRowKeys({
        id: datasetId,
        rowKeyColumns: selected,
      });
      if (cancelled) {
        return;
      }
      if (result.isErr()) {
        setCheckError(
          result.error.t === 'application'
            ? result.error.err.message ?? 'Could not check this key'
            : 'Could not check this key'
        );
        setChecking(false);
        return;
      }
      setCheck(result.value);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId, selected]);

  /**
   * The row id and business columns are alternatives, never a compound key.
   * Pairing them would produce something no more durable than the row id alone
   * while reading like a business key, so picking either side clears the other.
   */
  const toggle = (name: string): void => {
    setSelected((current) => {
      if (current.includes(name)) {
        return current.filter((column) => column !== name);
      }
      if (isInternalDatasetColumn(name)) {
        return [name];
      }
      return [
        ...current.filter((column) => !isInternalDatasetColumn(column)),
        name,
      ];
    });
  };

  /** Keyed, but by a number the next upload reassigns. */
  const internalKey = isInternalRowKeyDeclaration(selected);
  const passes = useMemo(
    () => check !== null && rowKeyCheckPasses(check),
    [check]
  );
  const canSubmit =
    !saving &&
    !checking &&
    selected.length > 0 &&
    passes &&
    datasetId !== null &&
    checkError === null;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setSaving(true);
    await onRespond({ rowKeyColumns: selected });
    setSaving(false);
  };

  return (
    <ModalPageView
      title="Set row keys"
      subtitle="Choose the columns that identify a row across re-uploads"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Re-uploading a CSV renumbers every row. Without a business key,
            workflows watching this dataset treat the whole file as new rows and
            run again over all of them, and the runs already recorded stop
            lining up with the rows that produced them. Pick columns whose
            combination is unique and always filled in.
          </span>
        </div>

        {loadError !== null ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : columns === null ? (
          <div className="flex justify-center py-6">
            <Loading.Spinner variant="sm" />
          </div>
        ) : columns.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This dataset has no columns yet. Upload data before setting a row
            key.
          </p>
        ) : (
          <>
            <div className="flex max-h-[min(40vh,20rem)] flex-col gap-2 overflow-y-auto">
              {columns.map((column) => {
                const index = selected.indexOf(column.name);
                const internal = isInternalDatasetColumn(column.name);
                return (
                  <label
                    key={column.name}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={index !== -1}
                      onCheckedChange={() => {
                        toggle(column.name);
                      }}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium">
                        {internal ? rowIdColumnLabel(column.name) : column.name}
                      </span>
                      {internal ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          assigned on upload
                        </span>
                      ) : column.type === null ? null : (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {column.type}
                        </span>
                      )}
                    </span>
                    {index === -1 || internal ? null : (
                      // The position is the point: it is part of the key.
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {index + 1}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <RowKeyCheckResult
              selected={selected}
              internal={internalKey}
              checking={checking}
              check={check}
              error={checkError}
            />
            <RowKeyAutomationImpact check={check} />
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={saving}
          >
            Cancel
          </Button>
          {/* Distinct from Cancel: this declines the key and lets whatever
              asked for it carry on, where Cancel abandons that too. Only
              offered when there is something to carry on to. */}
          {allowSkip ? (
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() =>
                void onRespond({ rowKeyColumns: [], skipped: true })
              }
            >
              Continue without one
            </Button>
          ) : null}
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {saving ? <Loading.Spinner variant="sm" /> : 'Set row keys'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};

/**
 * What declaring this key does to the workflows already watching the dataset.
 *
 * Worth saying out loud, because the honest answer is not "nothing". Changing
 * the key changes what identifies a row, and the rows that have already been
 * worked are carried across so they will not run again — but the rows that
 * never ran stay eligible, and for a dataset that has only ever been partly
 * automated that can be most of it. This is where a user finds that out, rather
 * than in their run history afterwards.
 *
 * Silent when nothing watches the dataset: there is no consequence to warn
 * about, and a "0 workflows" line is noise on the common path.
 */
export const RowKeyAutomationImpact: React.FC<{
  check: RowKeyCheck | null;
}> = ({ check }) => {
  if (check === null || check.automations.watchingWorkflows === 0) {
    return null;
  }
  const { watchingWorkflows, rowsWithRuns } = check.automations;
  // Never negative: both numbers come from the same response, but they count
  // different things and an artifact rewritten between them could cross over.
  const untouched = Math.max(0, check.totalRows - rowsWithRuns);
  return (
    <div className="flex items-start gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <Workflow className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {watchingWorkflows === 1
          ? '1 workflow watches this dataset. '
          : `${watchingWorkflows.toLocaleString()} workflows watch this dataset. `}
        {rowsWithRuns.toLocaleString()}{' '}
        {rowsWithRuns === 1 ? 'row has' : 'rows have'} already run and will be
        left alone. {untouched.toLocaleString()}{' '}
        {untouched === 1 ? 'row has' : 'rows have'} not, and may trigger.
      </span>
    </div>
  );
};

/** What the candidate key does to the rows, in the terms the user picked it in. */
const RowKeyCheckResult: React.FC<{
  selected: string[];
  /** The row id rather than the user's data — unique, but only until the next upload. */
  internal: boolean;
  checking: boolean;
  check: RowKeyCheck | null;
  error: string | null;
}> = ({ selected, internal, checking, check, error }) => {
  if (selected.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Pick one or more columns. Their combined value has to be unique across
        every row.
      </p>
    );
  }
  if (checking) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loading.Spinner variant="sm" />
        Checking {selected.join(' + ')} against the dataset…
      </div>
    );
  }
  if (error !== null) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        {error}
      </div>
    );
  }
  if (check === null) {
    return null;
  }
  if (rowKeyCheckPasses(check)) {
    // Uniqueness is not the question the row id fails. It is unique by
    // construction and says nothing at all about the next upload, so
    // congratulating the user on it would be the one message that leaves them
    // worse informed than no message.
    return internal ? (
      <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Rows are identified by their position in the file. That is unique
          today, but the next upload renumbers every row — pick columns from the
          data to keep identity across uploads.
        </span>
      </div>
    ) : (
      <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Unique across all {check.totalRows.toLocaleString()} rows. Every row
          keeps its identity through a re-upload.
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
      {check.duplicateRows > 0 ? (
        <span>
          {check.duplicateRows.toLocaleString()} of{' '}
          {check.totalRows.toLocaleString()} rows share a key with another row.
          Those rows would be treated as one — add another column to tell them
          apart.
        </span>
      ) : null}
      {check.unidentifiableRows > 0 ? (
        <span>
          {check.unidentifiableRows.toLocaleString()} rows have no value in one
          of these columns, so they cannot be identified at all and no workflow
          would ever see them.
        </span>
      ) : null}
      {check.examples.length === 0 ? null : (
        <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[11px] opacity-80">
          {check.examples.map((example) => (
            <li key={example.values.join(' ')}>
              {example.values.join(' · ')} — {example.rowCount} rows
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
